import React from "react";
import HQBadge from "./HQBadge";
import { useAuth } from "../../context/AuthContext";

/**
 * Phase 1 — PageHeader
 *
 * Standardised top-of-page block. Establishes the spacing rhythm and
 * the permanent HQ-badge placement (top-right of every admin/manager
 * page) so the corporate context never appears in different spots on
 * different pages.
 *
 *   <PageHeader
 *     eyebrow="Control Room"
 *     title="Dashboard"
 *     subtitle="Tap any card to drill in."
 *   />
 *
 * Optional:
 *   actions={<><Button .../><Button .../></>}
 *
 * Behavior:
 *   - For admin role  → renders <HQBadge>HQ</HQBadge> in the top-right.
 *   - For manager     → renders <HQBadge tone="brand">store name</HQBadge>.
 *   - For employee    → no badge (their layout already shows scope).
 *
 * Frontend display only — does NOT modify any store record, store ID,
 * company ID, or backend state.
 */
export default function PageHeader({
  eyebrow,
  title,
  subtitle,
  rightSlot,            // optional override of the auto HQ badge
  actions,              // primary + secondary action row
  titleSuffix,          // inline element next to the title (e.g. unread count badge)
  className = "",
  "data-testid": testId,
}) {
  const { user } = useAuth();
  const role = user?.role;

  let autoBadge = null;
  if (role === "admin") {
    autoBadge = <HQBadge data-testid="hq-badge-admin">HQ</HQBadge>;
  } else if (role === "manager") {
    autoBadge = (
      <HQBadge tone="brand" data-testid="hq-badge-manager">
        {user?.store_name || "Store"}
      </HQBadge>
    );
  }
  const badge = rightSlot !== undefined ? rightSlot : autoBadge;

  return (
    <header
      className={`mb-6 ${className}`}
      data-testid={testId || "page-header"}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="data-sku mb-2" data-testid="page-header-eyebrow">
              {eyebrow}
            </div>
          )}
          {title && (
            <h1
              className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05] mb-2 flex items-center gap-3 flex-wrap"
              data-testid="page-header-title"
            >
              <span className="break-words">{title}</span>
              {titleSuffix}
            </h1>
          )}
          {subtitle && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="page-header-subtitle"
            >
              {subtitle}
            </p>
          )}
        </div>
        {badge && (
          <div className="shrink-0 pt-1" data-testid="page-header-right-slot">
            {badge}
          </div>
        )}
      </div>

      {actions && (
        <div
          className="mt-5 flex items-center gap-2 flex-wrap"
          data-testid="page-header-actions"
        >
          {actions}
        </div>
      )}
    </header>
  );
}
